// A window wrapper that adds a floating "maximize / restore" button and can
// expand its content to fill the page's `.page-content` overlay (see
// MaximizeHost). Non-invasive: the button is an absolutely-positioned overlay
// in the wrapper's corner, so it never touches the wrapped chart/panel internals
// (their own gear / toolbox / toggles keep working).
//
// Instance-preserving move (react-reverse-portal pattern): the content is ALWAYS
// rendered (via createPortal) into ONE stable detached <div> that lives for the
// component's lifetime. To maximize / restore we physically appendChild that
// stable node between an in-place placeholder and the host overlay. Because the
// portal target node object never changes, React never unmounts the subtree —
// the echarts / Leaflet instance survives the move, and its size-sensor /
// trackResize redraws it at the new size (plus MaximizeHost dispatches a
// window 'resize' for good measure).
import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMaximize } from "./MaximizeHost";

function MaximizeButton({ isMax, onClick, style }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={isMax ? "Restore" : "Maximize"}
      aria-label={isMax ? "Restore panel" : "Maximize panel"}
      style={{
        position: "absolute",
        top: 6,
        right: 8,
        zIndex: 20,
        width: 22,
        height: 22,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        border: "1px solid rgba(223,227,255,0.25)",
        borderRadius: 4,
        background: hover ? "rgba(58,99,192,0.9)" : "rgba(26,31,56,0.55)",
        color: hover ? "#ffffff" : "rgba(223,227,255,0.78)",
        cursor: "pointer",
        lineHeight: 1,
        fontSize: 13,
        transition: "background 0.15s ease, color 0.15s ease",
        ...style,
      }}
    >
      {isMax ? "⤡" : "⤢"}
    </button>
  );
}

/**
 * @param {string}  id            Stable unique id for this window.
 * @param {string=} className     Layout classes for the in-place slot (e.g. the
 *                                original wrapper's "h-100 pe-1").
 * @param {object=} style         Extra style for the in-place slot.
 * @param {object=} buttonStyle   Per-panel nudge for the maximize button so it
 *                                clears existing header controls (gear/toggles).
 */
export default function MaximizablePanel({
  id,
  className,
  style,
  buttonStyle,
  children,
}) {
  const ctx = useMaximize();
  const maximizedId = ctx ? ctx.maximizedId : null;
  const setMaximizedId = ctx ? ctx.setMaximizedId : null;
  const hostRef = ctx ? ctx.hostRef : null;
  const isMax = maximizedId === id;

  const placeholderRef = useRef(null);
  const elRef = useRef(null);
  if (elRef.current === null) {
    const el = document.createElement("div");
    el.style.height = "100%";
    el.style.width = "100%";
    elRef.current = el;
  }

  // Re-parent the stable content node: into the host overlay when maximized,
  // back to the in-place placeholder when not.
  useLayoutEffect(() => {
    const el = elRef.current;
    const parent =
      isMax && hostRef && hostRef.current
        ? hostRef.current
        : placeholderRef.current;
    if (parent && el.parentNode !== parent) {
      parent.appendChild(el);
    }
  }, [isMax, hostRef]);

  const content = (
    <div
      style={{
        position: "relative",
        height: "100%",
        width: "100%",
        // Opaque bg while maximized so nothing shows through the overlay.
        ...(isMax ? { background: "#1a1f38" } : null),
      }}
    >
      <MaximizeButton
        isMax={isMax}
        onClick={() =>
          setMaximizedId && setMaximizedId(isMax ? null : id)
        }
        style={buttonStyle}
      />
      {children}
    </div>
  );

  return (
    <>
      {/* In-place slot: keeps the panel's layout footprint; the content node is
          appended here when not maximized. Carries the original layout classes. */}
      <div
        ref={placeholderRef}
        className={className}
        style={{ height: "100%", width: "100%", minHeight: 0, ...style }}
      />
      {createPortal(content, elRef.current)}
    </>
  );
}
